import { getAddress } from 'viem';
import { useSignMessage } from 'wagmi';
import { useCallback } from 'react';
import { AuthConnectRequest } from '@/api/spec.ts';
import useAuthStore from '../../../store/rootStore.ts';
import { useAuthSignatureMutation } from '../authHooks.query.ts';
import { useDialogStore } from '@/components/dialogs';
import { notify } from '@/feature/notifications';
import { AUTH_PAYLOAD_TTL } from '@/common/timeout_constants.ts';
import { telemetry } from '../../../utils/telemetry';
import { captureError, isUserRejection } from '../../../utils/sentryHelpers';
import { useEnsureChain } from '../../../hooks/useEnsureChain';

let isSigningLock = false;

export function useWalletSignAuth() {
  const { signMessageAsync, isPending: isSignPending, reset: resetSignMessage } = useSignMessage();
  const address = useAuthStore((s) => s.address);
  const chainId = useAuthStore((s) => s.config?.chain.id);
  const authPayload = useAuthStore((s) => s.authPayload);
  const authNonce = useAuthStore((s) => s.authNonce);
  const inviteCode = useAuthStore((s) => s.inviteCode);
  const { mutate: authorizeSignature, isPending: isLoading, error } = useAuthSignatureMutation();
  const openDialog = useDialogStore((s) => s.openDialog);
  const { ensureChain } = useEnsureChain();

  const agreeAndSign = useCallback(async () => {
    const { termsAccepted, setAuthPayload } = useAuthStore.getState();
    if (address && !termsAccepted[address]?.mandatory) {
      openDialog('termsDialog');
      return;
    }

    const isReadyToSign =
      !isSigningLock && !authNonce && !isSignPending && authPayload?.message && address != null && chainId != null;
    if (!isReadyToSign) {
      return;
    }

    if (authPayload.issued_at && Date.now() - authPayload.issued_at > AUTH_PAYLOAD_TTL) {
      setAuthPayload(undefined); // nonce expired before signing
      return;
    }

    try {
      isSigningLock = true;
      await ensureChain();
      const signature = await signMessageAsync({
        account: address,
        message: authPayload.message!,
      });

      if (signature == null) {
        throw new Error('Signature not obtained.');
      }

      if (authPayload.issued_at && Date.now() - authPayload.issued_at > AUTH_PAYLOAD_TTL) {
        setAuthPayload(undefined);
        notify('Nonce expired. Please sign again.', 'warning');
        return;
      }

      const requestData: AuthConnectRequest = {
        ...authPayload,
        address,
        chain_id: chainId,
        signature,
        ref_code: inviteCode,
      };

      return new Promise<void>((resolve, reject) => {
        authorizeSignature(requestData, {
          onSuccess: (data) => {
            useAuthStore.getState().authenticate(data, getAddress(requestData.address), requestData.chain_id);
            telemetry.logTermsAccepted(getAddress(requestData.address));
            telemetry.logOnboarding('authenticated', { via: 'signature' });
            resolve();
          },
          onError: (err: any) => {
            const { ref_code, ...rest } = requestData;

            switch (err.response?.status) {
              case 418:
              case 423:
                useAuthStore.getState().setPendingAccessRequest({
                  signedRequest: rest,
                  errorStatus: err.response?.status,
                });
                telemetry.logOnboarding('failed', { step: 'auth', reason: 'access_code_required' });
                openDialog('accessRequestDialog');
                resolve();
                break;
              case 403:
                useAuthStore.getState().setRestricted();
                telemetry.logRestrictedAccess(getAddress(requestData.address));
                telemetry.logOnboarding('failed', { step: 'auth', reason: 'restricted' });
                useAuthStore.getState().setPendingAccessRequest({
                  signedRequest: rest,
                  errorStatus: 403,
                });
                openDialog('accessRequestDialog');
                resolve();
                break;
              case 451:
                useAuthStore.getState().blockByAuthority({ step: 'auth' });
                openDialog('geoBlockDialog');
                resolve();
                break;
              default:
                captureError(err, 'auth_signature_authorization');
                telemetry.logOnboarding('failed', { step: 'auth', reason: 'auth_error', status: err.response?.status ?? null });
                notify('Something went wrong. Please try again or contact us on Discord.', 'error', {
                  debugInfo: err instanceof Error ? err.message : 'Failed to verify signature',
                });
                useAuthStore.getState().resetToWalletConnected();
                reject(err);
            }
          },
        });
      });
    } catch (err) {
      if (!isUserRejection(err)) {
        captureError(err, 'wallet_sign_auth');
        console.error('Signing error:', err);
      }
      resetSignMessage();
      throw err;
    } finally {
      isSigningLock = false;
    }
  }, [
    authNonce,
    isSignPending,
    authPayload,
    address,
    chainId,
    signMessageAsync,
    authorizeSignature,
    inviteCode,
    openDialog,
    ensureChain,
    resetSignMessage,
  ]);

  return { agreeAndSign, isLoading: isLoading || isSignPending, error };
}
